import { Destination, FilterConfig, MappingMode, Source } from '../../../types/DataSyncItem';
import { DataSyncViewModel, FieldMappingEntry } from '../../../types/DataSyncViewModel';
import { FieldDefinition } from '../../../types/FieldDefinition';
import { resetDestinationFieldMappings, resetSourceFieldMappings } from '../../../lib/dataSync';

export interface FormState {
  viewModel: DataSyncViewModel;
  sourceFields: FieldDefinition[];
  destinationFields: FieldDefinition[];
  destinationFieldsLoading: boolean;
  fieldMappingMode: MappingMode | undefined;
}

export const FormActionType = {
  SET_NAME: 'SET_NAME',
  SET_INTERVAL: 'SET_INTERVAL',
  SET_FIELD_MAPPINGS: 'SET_FIELD_MAPPINGS',
  SOURCE_CHANGED: 'SOURCE_CHANGED',
  DESTINATION_CHANGED: 'DESTINATION_CHANGED',
  INHERIT_SCHEMA: 'INHERIT_SCHEMA',
  RESET_FIXED_MODE: 'RESET_FIXED_MODE',
  INHERITED_SCHEMA_SYNCED: 'INHERITED_SCHEMA_SYNCED',
  SET_SOURCE_FIELDS: 'SET_SOURCE_FIELDS',
  SET_DESTINATION_FIELDS: 'SET_DESTINATION_FIELDS',
  SET_MAPPING_MODE: 'SET_MAPPING_MODE',
  SET_FILTER_CONFIG: 'SET_FILTER_CONFIG',
} as const;

export type FormAction =
  | { type: typeof FormActionType.SET_NAME; name: string }
  | { type: typeof FormActionType.SET_INTERVAL; interval: string }
  | { type: typeof FormActionType.SET_FIELD_MAPPINGS; mappings: FieldMappingEntry[] }
  | { type: typeof FormActionType.SOURCE_CHANGED; source: Source; sourceFields: FieldDefinition[] }
  | {
      type: typeof FormActionType.DESTINATION_CHANGED;
      destination: Destination;
      destinationFields: FieldDefinition[];
    }
  | {
      type: typeof FormActionType.INHERIT_SCHEMA;
      destination: Destination;
      mappings: FieldMappingEntry[];
      destinationFields: FieldDefinition[];
    }
  | { type: typeof FormActionType.RESET_FIXED_MODE }
  | {
      type: typeof FormActionType.INHERITED_SCHEMA_SYNCED;
      mappings: FieldMappingEntry[];
      destinationFields: FieldDefinition[];
    }
  | { type: typeof FormActionType.SET_SOURCE_FIELDS; sourceFields: FieldDefinition[] }
  | { type: typeof FormActionType.SET_DESTINATION_FIELDS; destinationFields: FieldDefinition[] }
  | { type: typeof FormActionType.SET_MAPPING_MODE; mode: MappingMode }
  | { type: typeof FormActionType.SET_FILTER_CONFIG; filterConfig: FilterConfig | undefined };

export function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case FormActionType.SET_NAME:
      return {
        ...state,
        viewModel: { ...state.viewModel, name: action.name },
      };

    case FormActionType.SET_INTERVAL:
      return {
        ...state,
        viewModel: { ...state.viewModel, interval: action.interval },
      };

    case FormActionType.SET_FIELD_MAPPINGS:
      return {
        ...state,
        viewModel: { ...state.viewModel, fieldMappings: action.mappings },
      };

    case FormActionType.SOURCE_CHANGED: {
      const productChanged = state.viewModel.source.product !== action.source.product;
      const objectChanged =
        state.viewModel.source.object.object_name !== action.source.object.object_name;
      const hasMappings = state.viewModel.fieldMappings.length > 0;
      const fieldMappings =
        objectChanged && hasMappings
          ? resetSourceFieldMappings(state.viewModel.fieldMappings)
          : state.viewModel.fieldMappings;
      // The filter expression is bound to the source schema's locale_config; once
      // the product or object changes, the previous expression no longer maps to a
      // visible UI control and would otherwise be silently shipped on save.
      const filterConfig =
        productChanged || objectChanged ? undefined : state.viewModel.filterConfig;

      return {
        ...state,
        viewModel: { ...state.viewModel, source: action.source, fieldMappings, filterConfig },
        sourceFields: action.sourceFields,
      };
    }

    case FormActionType.DESTINATION_CHANGED: {
      const objectChanged =
        state.viewModel.destination.object.object_name !== action.destination.object.object_name;
      const hasMappings = state.viewModel.fieldMappings.length > 0;
      const fieldMappings =
        objectChanged && hasMappings
          ? resetDestinationFieldMappings(state.viewModel.fieldMappings)
          : state.viewModel.fieldMappings;
      // Only show loading when switching to a new valid object (an async fetch will follow).
      // Clearing the object (e.g. product change) should not trigger loading since
      // there is nothing to fetch.
      const hasNewObject = objectChanged && !!action.destination.object.object_name;

      return {
        ...state,
        viewModel: { ...state.viewModel, destination: action.destination, fieldMappings },
        destinationFields: hasNewObject ? state.destinationFields : action.destinationFields,
        destinationFieldsLoading: hasNewObject,
      };
    }

    case FormActionType.INHERIT_SCHEMA:
      return {
        ...state,
        viewModel: {
          ...state.viewModel,
          destination: action.destination,
          fieldMappings: action.mappings,
        },
        destinationFields: action.destinationFields,
        destinationFieldsLoading: false,
        fieldMappingMode: MappingMode.FIXED,
      };

    case FormActionType.RESET_FIXED_MODE:
      if (state.fieldMappingMode !== MappingMode.FIXED) {
        return state;
      }
      return {
        ...state,
        viewModel: { ...state.viewModel, fieldMappings: [] },
        fieldMappingMode: MappingMode.EDITABLE,
      };

    case FormActionType.INHERITED_SCHEMA_SYNCED:
      return {
        ...state,
        viewModel: { ...state.viewModel, fieldMappings: action.mappings },
        destinationFields: action.destinationFields,
      };

    case FormActionType.SET_SOURCE_FIELDS:
      return { ...state, sourceFields: action.sourceFields };

    case FormActionType.SET_DESTINATION_FIELDS:
      return { ...state, destinationFields: action.destinationFields, destinationFieldsLoading: false };

    case FormActionType.SET_MAPPING_MODE:
      return { ...state, fieldMappingMode: action.mode };

    case FormActionType.SET_FILTER_CONFIG:
      return {
        ...state,
        viewModel: { ...state.viewModel, filterConfig: action.filterConfig },
      };

    default:
      return state;
  }
}
